import React, { useState, useEffect, useCallback, useRef } from "react";
import { Layout, Button, notification, DatePicker, Space, message, Table, Tag, Card, Select } from "antd";
import { Row, Form, Col } from "antd";
import { DownloadOutlined, FilterOutlined, ClearOutlined, SearchOutlined } from "@ant-design/icons";
import * as XLSX from 'xlsx'; // For Excel export
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable'; 

import TopHeader from "../common/TopHeader/TopHeader";
import Sidebar from "../common/Sidebar/Sidebar";
import dayjs from "dayjs";
import ReportsApi from "../common/Apis/ReportsApi";
import stockAdjustmentLogApi from "../common/Apis/StockAdjustmentLogApi";

const StockAdjustmentReports = () => {
    const { Content } = Layout;
    const [formAdd] = Form.useForm();
    const [stockAdjustments, setStockAdjustments] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [totalAdjustmentValue, setTotalAdjustmentValue] = useState(0);
    const [exportLoading, setExportLoading] = useState(false);
    const [filterLoading, setFilterLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
    const CURRENCY = decodedToken.currency === "USD" ? "$" : "€";

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });

    // Filter states
    const [batchCodeFilter, setBatchCodeFilter] = useState(null);
    const [adjustedByFilter, setAdjustedByFilter] = useState(null);

    // Filter options
    const [batchOptions, setBatchOptions] = useState([]);
    const [adjustedByOptions, setAdjustedByOptions] = useState([]);

    const displayData = filteredData.length > 0 ? filteredData : stockAdjustments;

    const PageLoading = useCallback(() => {
        setIsLoading(true);
        setTimeout(() => {
            setIsLoading(false);
        }, 800);
    }, []);

    // Extract unique batch codes from data
    const extractBatchOptions = (data) => {
        const batches = new Set();
        data.forEach((item) => {
            if (item?.batchCode) {
                batches.add(item.batchCode);
            }
        });
        setBatchOptions(Array.from(batches).sort());
    };

    // Extract unique adjusted by names from data
    const extractAdjustedByOptions = (data) => {
        const adjustedBy = new Set();
        data.forEach((item) => {
            if (item?.adjustedBy) {
                adjustedBy.add(item.adjustedBy);
            }
        });
        setAdjustedByOptions(Array.from(adjustedBy).sort());
    };

    const applyFilters = useCallback((data) => {
        if (!data || data.length === 0) {
            setFilteredData([]);
            return;
        }

        let filtered = [...data];

        if (batchCodeFilter) {
            filtered = filtered.filter(
                item =>
                    item?.batchCode?.toLowerCase() === batchCodeFilter.toLowerCase()
            );
        }

        if (adjustedByFilter) {
            filtered = filtered.filter(
                item =>
                    item?.adjustedBy?.toLowerCase() === adjustedByFilter.toLowerCase()
            );
        }

        setFilteredData(filtered);

        const totalValue = filtered.reduce(
            (total, item) => total + Math.abs(parseFloat(item?.cost) || 0),
            0
        );

        setTotalAdjustmentValue(totalValue);

    }, [batchCodeFilter, adjustedByFilter]);

    useEffect(() => {
        if (stockAdjustments.length > 0) {
            extractBatchOptions(stockAdjustments);
            extractAdjustedByOptions(stockAdjustments);
            applyFilters(stockAdjustments);
        } else {
            setFilteredData([]);
            setTotalAdjustmentValue(0);
        }
    }, [
        stockAdjustments,
        batchCodeFilter,
        adjustedByFilter,
        applyFilters,
    ]);

    const resetFilters = () => {
        setBatchCodeFilter(null);
        setAdjustedByFilter(null);
    };

    // UPDATED: Proper PDF export using autoTable
    const handlePrintButtonClick2 = () => {
        const dataToExport = filteredData.length > 0 ? filteredData : stockAdjustments;
        
        if (!dataToExport || dataToExport.length === 0) {
            message.warning("No data available to export");
            return;
        }

        try {
            // Create PDF document
            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            });

            // Add Title
            doc.setFontSize(16);
            doc.text('Stock Adjustment Report', 14, 15);

            // Add Filter Information
            doc.setFontSize(10);
            let filterInfo = `Total Records: ${dataToExport.length}`;
            
            // Get date range from form
            const startDate = formAdd.getFieldValue('startDate');
            const endDate = formAdd.getFieldValue('endDate');
            if (startDate && endDate) {
                filterInfo += ` | Date Range: ${dayjs(startDate).format('DD/MM/YYYY')} - ${dayjs(endDate).format('DD/MM/YYYY')}`;
            }
            
            if (batchCodeFilter) {
                filterInfo += ` | Batch: ${batchCodeFilter}`;
            }
            if (adjustedByFilter) {
                filterInfo += ` | Adjusted By: ${adjustedByFilter}`;
            }
            
            doc.text(filterInfo, 14, 22);

            // Prepare table data with all fields
            const tableData = dataToExport.map((item) => [
                formatDate(item.adjustedTimestamp),
                item.batchCode || '-',
                item.productName || '-',
                formatQuantity(item.previousQuantity, item.unit),
                formatAdjustedQuantity(item),
                formatQuantity(item.newQuantity || item.quantity, item.unit),
                CurrencyConvert(Math.abs(parseFloat(item.cost) || 0)),
                item.adjustedBy || '-',
                item.reason || '-'
            ]);

            // Generate table using autoTable
            autoTable(doc, {
                startY: 28,
                head: [[
                    'Date',
                    'Batch Code',
                    'Product Description',
                    'Previous Qty',
                    'Adjusted Qty',
                    'New Qty',
                    'Cost',
                    'Adjusted By',
                    'Reason'
                ]],
                body: tableData,
                styles: {
                    fontSize: 7,
                    cellPadding: 1.5,
                    overflow: 'linebreak'
                },
                headStyles: {
                    fillColor: [79, 129, 189],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    fontSize: 8
                },
                alternateRowStyles: {
                    fillColor: [245, 245, 245]
                },
                margin: {
                    left: 8,
                    right: 8,
                    top: 30
                },
                columnStyles: {
                    0: { cellWidth: 22 },
                    1: { cellWidth: 25 },
                    2: { cellWidth: 35 },
                    3: { cellWidth: 20 },
                    4: { cellWidth: 25 },
                    5: { cellWidth: 20 },
                    6: { cellWidth: 20 },
                    7: { cellWidth: 25 },
                    8: { cellWidth: 30 }
                },
                didDrawPage: function(data) {
                    const pageCount = doc.internal.getNumberOfPages();
                    doc.setFontSize(8);
                    doc.text(
                        `Page ${data.pageNumber} of ${pageCount}`,
                        doc.internal.pageSize.width - 20,
                        doc.internal.pageSize.height - 10
                    );
                    
                    doc.text(
                        `Generated: ${dayjs().format('DD/MM/YYYY HH:mm:ss')}`,
                        14,
                        doc.internal.pageSize.height - 10
                    );
                }
            });

            // Generate filename
            let fileName = `Stock_Adjustment_Report_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.pdf`;

            if (startDate && endDate) {
                const startStr = dayjs(startDate).format('YYYY-MM-DD');
                const endStr = dayjs(endDate).format('YYYY-MM-DD');
                const batchStr = batchCodeFilter ? `_${batchCodeFilter}` : '';
                const adjustedStr = adjustedByFilter ? `_${adjustedByFilter}` : '';
                fileName = `Stock_Adjustment_Report_${startStr}_to_${endStr}${batchStr}${adjustedStr}.pdf`;
            }

            // Save the PDF
            doc.save(fileName);
            message.success(`Successfully exported ${dataToExport.length} records to PDF!`);
        } catch (error) {
            console.error("Error exporting PDF:", error);
            message.error("Failed to export PDF");
        }
    };

    // Helper function to format quantity
    const formatQuantity = (qty, unit) => {
        const value = Number(qty || 0);
        const normalStr = value.toLocaleString("fullwide", {
            useGrouping: false,
            maximumFractionDigits: 20,
        });
        const match = normalStr.match(/^-?\d+(?:\.\d{0,2})?/);
        return `${match ? match[0] : "0"} ${unit || ''}`.trim();
    };

    // Helper function to format adjusted quantity for PDF
    const formatAdjustedQuantity = (record) => {
        const previousQty = record?.previousQuantity || record?.oldQuantity || 0;
        const newQty = record?.quantity || record?.newQuantity || 0;
        const difference = newQty - previousQty;
        const isIncrease = difference > 0;
        const isDecrease = difference < 0;
        
        const formatQty = (value) => {
            const normalStr = Number(value || 0).toLocaleString("fullwide", {
                useGrouping: false,
                maximumFractionDigits: 20,
            });
            const match = normalStr.match(/^-?\d+(?:\.\d{0,2})?/);
            return match ? match[0] : "0";
        };
        
        let sign = '';
        let displayQty = difference;
        
        if (isIncrease) {
            sign = '+';
        } else if (isDecrease) {
            sign = '-';
        } else {
            sign = '';
            displayQty = 0;
        }
        
        const formattedDiff = formatQty(Math.abs(displayQty));
        const formattedPrevious = formatQty(previousQty);
        const formattedNew = formatQty(newQty);
        const unit = record?.unit || '';
        
        return `${sign}${formattedDiff} ${unit} (${formattedPrevious} → ${formattedNew})`;
    };

    const handleFilter = (data) => {
        setFilterLoading(true);
            const startDateN = new Date(data.startDate).getTime();        
            const endDateN = new Date(data.endDate).getTime();

        stockAdjustmentLogApi.getAllStockAdjustments(0, pagination.pageSize, startDateN, endDateN)
            .then((response) => {
                const adjustments = response?.payloadDto || [];
                setStockAdjustments(adjustments);
                
                if (adjustments.length > 0) {
                    extractBatchOptions(adjustments);
                    extractAdjustedByOptions(adjustments);
                }
                
                setBatchCodeFilter(null);
                setAdjustedByFilter(null);
                
                setPagination({
                    current: 1,
                    pageSize: 10,
                    total: adjustments.length 
                });
                
                PageLoading();
                setFilterLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setFilterLoading(false);
                if (err.apiError?.status) {
                    notification.open({
                        message: (
                            <span style={{ fontWeight: "bold" }}>{err.apiError.status}</span>
                        ),
                        description: err.apiError.message,
                        closeIcon: false,
                        duration: 3,
                        style: {
                            backgroundColor: "#fff2f0",
                            color: "red",
                        },
                    });
                }
            });
    };

    const fetchStockAdjustments = useCallback(async (page = 1, pageSize = 10) => {
        try {
            PageLoading();
            const response = await ReportsApi.StockAdjustmentReport(page - 1, pageSize);
            setStockAdjustments(response?.payloadDto || []);
            setPagination({
                current: response.number + 1,
                pageSize: response.size,
                total: response.totalElements
            });
        } catch (err) {
            console.log(err);
        }
    }, [PageLoading]);

    const handleTableChange = (newPagination) => {
        fetchStockAdjustments(newPagination.current, newPagination.pageSize);
    };

    function formatDate(date) {
        if (!date) return '-';
        const dateNew = parseInt(date, 10);
        const options = { day: "2-digit", month: "short", year: "numeric" };
        const formattedDate = new Date(dateNew).toLocaleDateString("en-US", options);
        const [month, day, year] = formattedDate.split(" ");
        const capitalizedMonth = month.toUpperCase();
        return `${day} ${capitalizedMonth} ${year}`;
    }

    function convertToUnixTimestamp(dateString) {
        const parts = dateString.split("/");
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        
        const unixTimestamp = Date.UTC(year, month, day);
        return unixTimestamp;
    }

    const handleClear = () => {
        setIsLoading(true);
        setTotalAdjustmentValue(0);
        setStockAdjustments([]);
        setFilteredData([]);
        formAdd.resetFields();
        setBatchCodeFilter(null);
        setAdjustedByFilter(null);
        const tomorrow = dayjs().add(1, 'day');
        const today = dayjs();
        formAdd.setFieldsValue({
            startDate: today,
            endDate: tomorrow,
        });
        setTimeout(() => {
            setIsLoading(false);
            fetchStockAdjustments(1, 10);
        }, 800);
    };

    const CurrencyConvert = (value) => {
        const formattedNumber = new Intl.NumberFormat('en-US').format(value);
        return formattedNumber;
    };

    const getAdjustmentTypeTag = (type) => {
        if (type === 'INCREASE' || type === 'ADDITION') {
            return <Tag color="green">Increase</Tag>;
        } else if (type === 'DECREASE' || type === 'REDUCTION') {
            return <Tag color="red">Decrease</Tag>;
        } else {
            return <Tag color="blue">{type}</Tag>;
        }
    };

    const hasActiveFilters = batchCodeFilter || adjustedByFilter;

    useEffect(() => {
        const tomorrow = dayjs().add(1, 'day');
        const today = dayjs();
        formAdd.setFieldsValue({
            startDate: today,
            endDate: tomorrow,
        });
        fetchStockAdjustments(1, 10);
    }, []);

    const columns = [
        {
            title: 'Date',
            dataIndex: 'adjustedTimestamp',
            key: 'adjustedTimestamp',
            width: '12%',
            render: (date) => formatDate(date),
        },
        {
            title: 'Batch Code',
            dataIndex: 'batchCode',
            key: 'batchCode',
            width: '12%',
            render: (batchCode) => (
                <div
                style={{
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    whiteSpace: 'nowrap',
                    maxWidth: '200px',
                    marginBottom: '0px',
                    marginTop: '-3px',
                    padding: '0px 0px 0px',
                    display: 'block',
                }}
                >
                {batchCode || '-'}
                </div>
            ),
        },
        {
            title: 'Product Description',
            dataIndex: 'productName',
            key: 'productName',
            width: '20%',
            render: (productName) => productName || '-',
        },
        {
            title: 'Previous Qty',
            dataIndex: 'previousQuantity',
            key: 'previousQuantity',
            width: '10%',
            align: 'right',
            render: (qty, record) => {
                const value = Number(qty || 0);
                const normalStr = value.toLocaleString("fullwide", {
                    useGrouping: false,
                    maximumFractionDigits: 20,
                });
                const match = normalStr.match(/^-?\d+(?:\.\d{0,2})?/);
                return `${match ? match[0] : "0"} ${record?.unit || ''}`;
            },
        },
        {
            title: 'Adjusted Qty',
            dataIndex: 'quantity',
            key: 'quantity',
            width: '10%',
            align: 'right',
            render: (qty, record) => {
                const previousQty = record?.previousQuantity || record?.oldQuantity || 0;
                const newQty = record?.quantity || record?.newQuantity || 0;
                const difference = newQty - previousQty;
                const isIncrease = difference > 0;
                const isDecrease = difference < 0;
                const isNoChange = difference === 0;
                
                const formatQty = (value) => {
                    const normalStr = Number(value || 0).toLocaleString("fullwide", {
                        useGrouping: false,
                        maximumFractionDigits: 20,
                    });
                    const match = normalStr.match(/^-?\d+(?:\.\d{0,2})?/);
                    return match ? match[0] : "0";
                };
                
                let color = 'black';
                let sign = '';
                let displayQty = difference;
                
                if (isIncrease) {
                    color = 'green';
                    sign = '+';
                } else if (isDecrease) {
                    color = 'red';
                    sign = '-';
                } else {
                    color = '#666';
                    sign = '';
                    displayQty = 0;
                }
                
                const formattedDiff = formatQty(Math.abs(displayQty));
                const formattedPrevious = formatQty(previousQty);
                const formattedNew = formatQty(newQty);
                const unit = record?.unit || '';
                const tooltipText = `Previous: ${formattedPrevious} ${unit}\nNew: ${formattedNew} ${unit}\nDifference: ${sign}${formattedDiff} ${unit}`;
                
                return (
                    <span 
                        style={{ 
                            color, 
                            fontWeight: 'bold',
                            cursor: 'pointer'
                        }}
                        title={tooltipText}
                    >
                        {isNoChange ? (
                            <span style={{ color: '#666' }}>0 {unit}</span>
                        ) : (
                            <span>
                                {sign}{formattedDiff} {unit}
                                <span style={{ 
                                    fontSize: '10px', 
                                    color: '#999', 
                                    fontWeight: 'normal',
                                    marginLeft: '4px',
                                    display: 'block'
                                }}>
                                    ({formattedPrevious} → {formattedNew})
                                </span>
                            </span>
                        )}
                    </span>
                );
            },
        },
        {
            title: 'New Qty',
            dataIndex: 'newQuantity',
            key: 'newQuantity',
            width: '10%',
            align: 'right',
            render: (qty, record) => {
                const value = Number(qty || 0);
                const normalStr = value.toLocaleString("fullwide", {
                    useGrouping: false,
                    maximumFractionDigits: 20,
                });
                const match = normalStr.match(/^-?\d+(?:\.\d{0,2})?/);
                return `${match ? match[0] : "0"} ${record?.unit || ''}`;
            },
        },
        {
            title: 'Cost',
            dataIndex: 'cost',
            key: 'cost',
            width: '10%',
            align: 'right',
            render: (cost) => `${CURRENCY} ${CurrencyConvert(Math.abs(parseFloat(cost) || 0))}`,
        },
        {
            title: 'Adjusted By',
            dataIndex: 'adjustedBy',
            key: 'adjustedBy',
            width: '10%',
            render: (adjustedBy) => adjustedBy || '-',
        },
        {
            title: 'Reason',
            dataIndex: 'reason',
            key: 'reason',
            width: '15%',
            ellipsis: true,
            render: (reason) => reason || '-',
        },
    ];

    const centeredColumnStyle = columns.map((column) => ({
        ...column,
        align: 'center'
    }));

    return (
        <Layout style={{ minHeight: "100vh" }}>
            <Sidebar />
            <Layout>
                <TopHeader onPrintResponse2={handlePrintButtonClick2} />
                {isLoading && (
                    <div className="spinner-overlay" id="spinnerOverlay">
                        <div className="spinner"></div>
                    </div>
                )}

                <Content
                    className="form-content nav-content"
                    style={{
                        margin: "14px 16px 0",
                        padding: "30px",
                        background: "white",
                    }}
                >
                    <div>
                        {/* Filter Section - Separated Card */}
                        <Card 
                            style={{ 
                                marginBottom: '24px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                            }}
                            bodyStyle={{ padding: '20px' }}
                        >
                            <div style={{ marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: '10px' }}>
                                <Space>
                                    <FilterOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                                    <span style={{ fontSize: '16px', fontWeight: 500, color: '#333' }}>Date Range Filter</span>
                                </Space>
                            </div>
                            
                            <Form
                                form={formAdd}
                                name="stock_adjustment_filter_form"
                                layout="vertical"
                                onFinish={handleFilter}
                            >
                                <Row gutter={[16, 0]}>
                                    <Col lg={8} md={12} sm={24} xs={24}>
                                        <Form.Item
                                            name="startDate"
                                            label="Start Date"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please Select Start Date.",
                                                },
                                            ]}
                                        >
                                            <DatePicker
                                                format={"DD/MM/YYYY"}
                                                placeholder="Select start date"
                                                style={{ width: '100%', height: '40px' }}
                                                allowClear={false}
                                                suffixIcon={null}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col lg={8} md={12} sm={24} xs={24}>
                                        <Form.Item
                                            name="endDate"
                                            label="End Date"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please Select End Date.",
                                                },
                                            ]}
                                        >
                                            <DatePicker
                                                format={"DD/MM/YYYY"}
                                                placeholder="Select end date"
                                                style={{ width: '100%', height: '40px' }}
                                                allowClear={false}
                                                suffixIcon={null}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col lg={8} md={24} sm={24} xs={24}>
                                        <Form.Item label="Actions">
                                            <Space size="middle" wrap>
                                                <Button
                                                    type="primary"
                                                    htmlType="submit"
                                                    loading={filterLoading}
                                                    icon={<FilterOutlined />}
                                                    style={{ height: '40px', minWidth: '100px' }}
                                                >
                                                    Apply Date Filter
                                                </Button>
                                                <Button
                                                    onClick={() => handleClear()}
                                                    icon={<ClearOutlined />}
                                                    style={{ height: '40px', minWidth: '100px' }}
                                                >
                                                    Clear All
                                                </Button>
                                            </Space>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Form>
                        </Card>

                        {/* Advanced Filters Section */}
                        <Card 
                            style={{ 
                                marginBottom: '24px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                background: '#fafafa'
                            }}
                            bodyStyle={{ padding: '20px' }}
                        >
                            <div style={{ marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: '10px' }}>
                                <Space>
                                    <SearchOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                                    <span style={{ fontSize: '16px', fontWeight: 500, color: '#333' }}>Advanced Filters</span>
                                    <span style={{ fontSize: '12px', color: '#999' }}>
                                        (Filter by Batch Code and Adjusted By)
                                    </span>
                                </Space>
                            </div>
                            
                            <Row gutter={[16, 16]}>
                                <Col lg={8} md={12} sm={12} xs={24}>
                                    <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 500, color: '#555' }}>
                                        Batch Code
                                    </div>
                                    <Select
                                        placeholder="All Batches"
                                        style={{ width: '100%' }}
                                        value={batchCodeFilter}
                                        onChange={setBatchCodeFilter}
                                        allowClear
                                        showSearch
                                        filterOption={(input, option) =>
                                            option?.children?.toLowerCase().includes(input.toLowerCase())
                                        }
                                    >
                                        {batchOptions.map(batch => (
                                            <Select.Option key={batch} value={batch}>{batch}</Select.Option>
                                        ))}
                                    </Select>
                                </Col>

                                <Col lg={8} md={12} sm={12} xs={24}>
                                    <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 500, color: '#555' }}>
                                        Adjusted By
                                    </div>
                                    <Select
                                        placeholder="All Adjusted By"
                                        style={{ width: '100%' }}
                                        value={adjustedByFilter}
                                        onChange={setAdjustedByFilter}
                                        allowClear
                                        showSearch
                                        filterOption={(input, option) =>
                                            option?.children?.toLowerCase().includes(input.toLowerCase())
                                        }
                                    >
                                        {adjustedByOptions.map(name => (
                                            <Select.Option key={name} value={name}>{name}</Select.Option>
                                        ))}
                                    </Select>
                                </Col>

                                <Col lg={8} md={12} sm={24} xs={24} style={{ display: 'flex', alignItems: 'flex-end' }}>
                                    <Space>
                                        <Button
                                            onClick={resetFilters}
                                            icon={<ClearOutlined />}
                                            style={{ height: '36px' }}
                                            disabled={!hasActiveFilters}
                                        >
                                            Reset Filters
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                            
                            <div style={{ marginTop: '12px', padding: '8px 12px', background: '#e6f7ff', borderRadius: '4px', fontSize: '12px', color: '#1890ff' }}>
                                <strong>Total Records:</strong> {stockAdjustments.length} | 
                                <strong> Displaying:</strong> {displayData.length} records
                                {hasActiveFilters && (
                                    <span style={{ marginLeft: '8px' }}>
                                        (Filtered by: 
                                        {batchCodeFilter && ` Batch: ${batchCodeFilter}`}
                                        {adjustedByFilter && `, Adjusted By: ${adjustedByFilter}`}
                                        )
                                    </span>
                                )}
                            </div>
                        </Card>

                        {/* Report Results Section */}
                        <Card 
                            style={{ 
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                            }}
                            bodyStyle={{ padding: '20px' }}
                        >
                            <div style={{ 
                                marginBottom: '16px', 
                                borderBottom: '1px solid #f0f0f0', 
                                paddingBottom: '10px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '10px'
                            }}>
                                <Space>
                                    <span style={{ fontSize: '16px', fontWeight: 500, color: '#333' }}>Stock Adjustment Report Results</span>
                                    <span style={{ fontSize: '12px', color: '#999' }}>
                                        {displayData.length > 0 && `(${displayData.length} records found)`}
                                        {displayData.length !== stockAdjustments.length && stockAdjustments.length > 0 && 
                                            ` (filtered from ${stockAdjustments.length} total)`
                                        }
                                    </span>
                                </Space>
                                <Space>
                                    <Button
                                        type="primary"
                                        onClick={handlePrintButtonClick2}
                                        icon={<DownloadOutlined />}
                                         style={{ 
                                            height: '36px',
                                            backgroundColor: '#52c41a',
                                            borderColor: '#52c41a'
                                        }}
                                    >
                                        Export PDF
                                    </Button>
                                    
                                </Space>
                            </div>
                            
                            <div className="dateFilterTable" style={{ overflowX: 'auto' }}>
                                <Table
                                    columns={centeredColumnStyle}
                                    dataSource={displayData}
                                    rowKey="id"
                                    pagination={{
                                        current: pagination.current,
                                        pageSize: pagination.pageSize,
                                        total: pagination.total,
                                        showSizeChanger: true,
                                        pageSizeOptions: ['10', '20', '50', '100', '500'],
                                        showTotal: (total, range) =>
                                            `${range[0]}-${range[1]} of ${total} items`
                                    }}
                                    onChange={handleTableChange}
                                    bordered
                                    className="users-table Reportable"
                                    scroll={{ x: 'max-content' }}
                                    summary={() => {
                                        return (
                                            <Table.Summary fixed>
                                                <Table.Summary.Row style={{ backgroundColor: '#f0f2f5', fontWeight: 'bold' }}>
                                                    <Table.Summary.Cell index={0} colSpan={7}>
                                                        <div style={{ textAlign: 'right', fontSize: '16px', fontWeight: 'bold' }}>
                                                            Total Adjustment Value:
                                                        </div>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell index={1} colSpan={2}>
                                                        <div style={{ fontSize: '16px', fontWeight: 'bold', textAlign: 'right' }}>
                                                            {CurrencyConvert(totalAdjustmentValue)}
                                                        </div>
                                                    </Table.Summary.Cell>
                                                </Table.Summary.Row>
                                            </Table.Summary>
                                        );
                                    }}
                                />
                            </div>
                        </Card>
                    </div>
                </Content>
            </Layout>
        </Layout>
    );
};

export default StockAdjustmentReports;